import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { motion } from 'framer-motion';

// API URL from environment variable or default
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';

// Create axios instance
const axiosInstance = axios.create({
  baseURL: API_URL,
  withCredentials: true,
});

const AdminFlowerPage = () => {
  const { isAdmin, currentUser } = useAuth();
  const navigate = useNavigate();
  const [flowers, setFlowers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('list');
  const [selectedFlower, setSelectedFlower] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    color: '',
    image_url: '',
    price_per_stem: 0,
    available: true,
    category: '',
  });
  const [categories, setCategories] = useState([]);
  const [colors, setColors] = useState([]);
  const [newCategory, setNewCategory] = useState('');
  const [newColor, setNewColor] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const fileInputRef = useRef(null);

  // Redirect if not admin
  useEffect(() => {
    if (currentUser && !isAdmin) {
      navigate('/');
    }
  }, [currentUser, isAdmin, navigate]);

  // Set up authorization header for all requests
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      axiosInstance.defaults.headers.common[
        'Authorization'
      ] = `Bearer ${token}`;
    }
  }, [currentUser]);

  // Fetch flowers, categories, and colors on component mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        // Fetch flowers
        const flowersRes = await axiosInstance.get('/flowers');
        setFlowers(flowersRes.data);

        // Fetch categories
        const categoriesRes = await axiosInstance.get('/flowers/categories');
        setCategories(categoriesRes.data);

        // Fetch colors
        const colorsRes = await axiosInstance.get('/flowers/colors');
        setColors(colorsRes.data);

        setLoading(false);
      } catch (err) {
        console.error('Error fetching data:', err);
        setError('Failed to fetch data. Please try again.');
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;

    // Update form data
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value,
    });

    // Reset new category/color input when selecting a different option
    if (name === 'category' && value !== 'other') {
      setNewCategory('');
    }

    if (name === 'color' && value !== 'other') {
      setNewColor('');
    }
  };

  // Handle new category input
  const handleNewCategoryChange = (e) => {
    setNewCategory(e.target.value);
    // Do not update formData.category here to keep it as 'other'
  };

  // Handle new color input
  const handleNewColorChange = (e) => {
    setNewColor(e.target.value);
    // Do not update formData.color here to keep it as 'other'
  };

  // Prepare form data for submission
  const prepareFormDataForSubmission = () => {
    const submissionData = { ...formData };

    // If category is 'other', use the newCategory value
    if (formData.category === 'other' && newCategory) {
      submissionData.category = newCategory;
    }

    // If color is 'other', use the newColor value
    if (formData.color === 'other' && newColor) {
      submissionData.color = newColor;
    }

    return submissionData;
  };

  // Handle file upload to Supabase storage
  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      setIsUploading(true);
      setUploadProgress(0);

      // Create FormData for file upload
      const formData = new FormData();
      formData.append('file', file);

      // Upload to backend endpoint that handles Supabase storage
      const response = await axiosInstance.post(
        `/upload/flower-image`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
          onUploadProgress: (progressEvent) => {
            const percentCompleted = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total
            );
            setUploadProgress(percentCompleted);
          },
        }
      );

      // Update form with the image URL from Supabase
      setFormData({
        ...formData,
        image_url: response.data.url,
      });

      setIsUploading(false);
    } catch (error) {
      console.error('Error uploading image:', error);
      setIsUploading(false);
      setError('Failed to upload image. Please try again.');
    }
  };

  // Handle form submission for creating a new flower
  const handleCreateFlower = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);

      // Validate price
      const price = parseFloat(formData.price_per_stem);
      if (isNaN(price) || price <= 0) {
        setError('Price must be a positive number');
        setLoading(false);
        return;
      }

      // Prepare data with custom category/color if needed
      const submissionData = prepareFormDataForSubmission();

      // Create flower
      const res = await axiosInstance.post('/flowers', {
        ...submissionData,
        price_per_stem: price,
      });

      // Add new flower to list and reset form
      setFlowers([...flowers, res.data]);
      setFormData({
        name: '',
        description: '',
        color: '',
        image_url: '',
        price_per_stem: 0,
        available: true,
        category: '',
      });
      setNewCategory('');
      setNewColor('');
      setActiveTab('list');
      setLoading(false);
    } catch (err) {
      console.error('Error creating flower:', err);
      setError(err.response?.data?.detail || 'Failed to create flower');
      setLoading(false);
    }
  };

  // Handle form submission for updating a flower
  const handleUpdateFlower = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);

      // Validate price
      const price = parseFloat(formData.price_per_stem);
      if (formData.price_per_stem && (isNaN(price) || price <= 0)) {
        setError('Price must be a positive number');
        setLoading(false);
        return;
      }

      // Prepare data with custom category/color if needed
      const submissionData = prepareFormDataForSubmission();

      // Update flower
      const res = await axiosInstance.put(`/flowers/${selectedFlower.id}`, {
        ...submissionData,
        price_per_stem: formData.price_per_stem ? price : undefined,
      });

      // Update flower in list
      setFlowers(
        flowers.map((flower) =>
          flower.id === selectedFlower.id ? res.data : flower
        )
      );

      setActiveTab('list');
      setSelectedFlower(null);
      setNewCategory('');
      setNewColor('');
      setLoading(false);
    } catch (err) {
      console.error('Error updating flower:', err);
      setError(err.response?.data?.detail || 'Failed to update flower');
      setLoading(false);
    }
  };

  // Handle deleting a flower
  const handleDeleteFlower = async (id) => {
    if (window.confirm('Are you sure you want to delete this flower?')) {
      try {
        setLoading(true);

        // Delete flower
        await axiosInstance.delete(`/flowers/${id}`);

        // Remove flower from list
        setFlowers(flowers.filter((flower) => flower.id !== id));

        setLoading(false);
      } catch (err) {
        console.error('Error deleting flower:', err);
        setError(err.response?.data?.detail || 'Failed to delete flower');
        setLoading(false);
      }
    }
  };

  // Handle toggling flower availability
  const handleToggleAvailability = async (id, currentAvailability) => {
    try {
      setLoading(true);

      // Toggle availability
      const res = await axiosInstance.put(`/flowers/${id}/availability`, {
        available: !currentAvailability,
      });

      // Update flower in list
      setFlowers(
        flowers.map((flower) => (flower.id === id ? res.data : flower))
      );

      setLoading(false);
    } catch (err) {
      console.error('Error toggling availability:', err);
      setError(err.response?.data?.detail || 'Failed to update availability');
      setLoading(false);
    }
  };

  // Handle editing a flower
  const handleEditFlower = (flower) => {
    setSelectedFlower(flower);
    setFormData({
      name: flower.name,
      description: flower.description || '',
      color: flower.color,
      image_url: flower.image_url || '',
      price_per_stem: flower.price_per_stem,
      available: flower.available,
      category: flower.category,
    });
    setActiveTab('edit');
  };

  // Render loading state
  if (loading && flowers.length === 0) {
    return (
      <div className="container mx-auto px-4 py-8">
        <h1 className="text-3xl font-bold mb-6">Flower Management</h1>
        <div className="flex justify-center items-center h-64">
          <div className="w-16 h-16 border-4 border-secondary border-t-transparent rounded-full animate-spin"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <h1 className="text-3xl font-bold mb-6">Flower Management</h1>

      {/* Error message */}
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      {/* Tabs */}
      <div className="flex mb-6 border-b">
        <button
          className={`px-4 py-2 ${
            activeTab === 'list'
              ? 'border-b-2 border-primary font-semibold'
              : ''
          }`}
          onClick={() => setActiveTab('list')}
        >
          Flower List
        </button>
        <button
          className={`px-4 py-2 ${
            activeTab === 'create'
              ? 'border-b-2 border-primary font-semibold'
              : ''
          }`}
          onClick={() => setActiveTab('create')}
        >
          Add New Flower
        </button>
      </div>

      {/* Flower List */}
      {activeTab === 'list' && (
        <div className="overflow-x-auto">
          <table className="min-w-full bg-white border border-gray-200">
            <thead>
              <tr>
                <th className="px-4 py-2 border">Image</th>
                <th className="px-4 py-2 border">Name</th>
                <th className="px-4 py-2 border">Category</th>
                <th className="px-4 py-2 border">Color</th>
                <th className="px-4 py-2 border">Price</th>
                <th className="px-4 py-2 border">Available</th>
                <th className="px-4 py-2 border">Actions</th>
              </tr>
            </thead>
            <tbody>
              {flowers.map((flower) => (
                <tr key={flower.id}>
                  <td className="px-4 py-2 border">
                    {flower.image_url ? (
                      <img
                        src={flower.image_url}
                        alt={flower.name}
                        className="w-16 h-16 object-cover"
                      />
                    ) : (
                      <div className="w-16 h-16 bg-gray-200 flex items-center justify-center text-gray-500">
                        No Image
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-2 border">{flower.name}</td>
                  <td className="px-4 py-2 border">{flower.category}</td>
                  <td className="px-4 py-2 border">
                    <div className="flex items-center">
                      <div
                        className="w-4 h-4 rounded-full mr-2"
                        style={{ backgroundColor: flower.color.toLowerCase() }}
                      ></div>
                      {flower.color}
                    </div>
                  </td>
                  <td className="px-4 py-2 border">
                    ₹{flower.price_per_stem.toFixed(2)}
                  </td>
                  <td className="px-4 py-2 border">
                    <button
                      onClick={() =>
                        handleToggleAvailability(flower.id, flower.available)
                      }
                      className={`px-2 py-1 rounded ${
                        flower.available
                          ? 'bg-green-100 text-green-800'
                          : 'bg-red-100 text-red-800'
                      }`}
                    >
                      {flower.available ? 'Available' : 'Unavailable'}
                    </button>
                  </td>
                  <td className="px-4 py-2 border">
                    <div className="flex space-x-2">
                      <button
                        onClick={() => handleEditFlower(flower)}
                        className="px-2 py-1 bg-blue-500 text-white rounded hover:bg-blue-600"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDeleteFlower(flower.id)}
                        className="px-2 py-1 bg-red-500 text-white rounded hover:bg-red-600"
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create Flower Form */}
      {activeTab === 'create' && (
        <form
          onSubmit={handleCreateFlower}
          className="bg-white p-6 rounded-lg shadow-md"
        >
          <h2 className="text-xl font-semibold mb-4">Add New Flower</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="mb-4">
              <label className="block text-gray-700 mb-2" htmlFor="name">
                Name *
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border rounded-md"
                required
              />
            </div>

            <div className="mb-4">
              <label className="block text-gray-700 mb-2" htmlFor="category">
                Category *
              </label>
              <select
                id="category"
                name="category"
                value={formData.category}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border rounded-md"
                required
              >
                <option value="">Select Category</option>
                {categories.map((category) => (
                  <option key={category} value={category}>
                    {category}
                  </option>
                ))}
                <option value="other">Other (New Category)</option>
              </select>

              {formData.category === 'other' && (
                <input
                  type="text"
                  name="newCategory"
                  placeholder="Enter new category"
                  value={newCategory}
                  onChange={handleNewCategoryChange}
                  className="w-full px-3 py-2 border rounded-md mt-2"
                  required
                />
              )}
            </div>

            <div className="mb-4">
              <label className="block text-gray-700 mb-2" htmlFor="color">
                Color *
              </label>
              <select
                id="color"
                name="color"
                value={formData.color}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border rounded-md"
                required
              >
                <option value="">Select Color</option>
                {colors.map((color) => (
                  <option key={color} value={color}>
                    {color}
                  </option>
                ))}
                <option value="other">Other (New Color)</option>
              </select>

              {formData.color === 'other' && (
                <input
                  type="text"
                  name="newColor"
                  placeholder="Enter new color"
                  value={newColor}
                  onChange={handleNewColorChange}
                  className="w-full px-3 py-2 border rounded-md mt-2"
                  required
                />
              )}
            </div>

            <div className="mb-4">
              <label
                className="block text-gray-700 mb-2"
                htmlFor="price_per_stem"
              >
                Price Per Stem (₹) *
              </label>
              <input
                type="number"
                id="price_per_stem"
                name="price_per_stem"
                value={formData.price_per_stem}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border rounded-md"
                min="0.01"
                step="0.01"
                required
              />
            </div>

            <div className="mb-4">
              <label
                className="block text-gray-700 mb-2"
                htmlFor="image_upload"
              >
                Flower Image
              </label>
              <div className="flex flex-col space-y-2">
                <input
                  type="file"
                  id="image_upload"
                  name="image_upload"
                  accept="image/jpeg, image/png, image/gif, image/webp"
                  onChange={handleFileUpload}
                  className="w-full px-3 py-2 border rounded-md"
                  ref={fileInputRef}
                />
                <small className="text-gray-500">
                  Supported formats: JPEG, PNG, GIF, WebP
                </small>

                {isUploading && (
                  <div className="w-full bg-gray-200 rounded-full h-2.5">
                    <div
                      className="bg-secondary h-2.5 rounded-full"
                      style={{ width: `${uploadProgress}%` }}
                    ></div>
                  </div>
                )}

                {formData.image_url && (
                  <div className="flex items-center mt-2">
                    <img
                      src={formData.image_url}
                      alt="Flower preview"
                      className="w-16 h-16 object-cover rounded mr-2"
                    />
                    <span className="text-sm text-gray-500 truncate flex-1">
                      {formData.image_url}
                    </span>
                  </div>
                )}

                <div className="text-sm text-gray-500">
                  Or provide an image URL:
                </div>

                <input
                  type="url"
                  id="image_url"
                  name="image_url"
                  value={formData.image_url}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border rounded-md"
                  placeholder="https://example.com/image.jpg"
                />
              </div>
            </div>

            <div className="mb-4">
              <label className="block text-gray-700 mb-2" htmlFor="available">
                Availability
              </label>
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="available"
                  name="available"
                  checked={formData.available}
                  onChange={handleInputChange}
                  className="mr-2"
                />
                <span>Available for purchase</span>
              </div>
            </div>
          </div>

          <div className="mb-4">
            <label className="block text-gray-700 mb-2" htmlFor="description">
              Description
            </label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border rounded-md"
              rows="4"
            ></textarea>
          </div>

          <div className="flex justify-end">
            <button
              type="button"
              onClick={() => setActiveTab('list')}
              className="px-4 py-2 bg-gray-300 text-gray-800 rounded-md mr-2"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-primary text-white rounded-md"
              disabled={loading}
            >
              {loading ? 'Saving...' : 'Save Flower'}
            </button>
          </div>
        </form>
      )}

      {/* Edit Flower Form */}
      {activeTab === 'edit' && selectedFlower && (
        <form
          onSubmit={handleUpdateFlower}
          className="bg-white p-6 rounded-lg shadow-md"
        >
          <h2 className="text-xl font-semibold mb-4">Edit Flower</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="mb-4">
              <label className="block text-gray-700 mb-2" htmlFor="edit-name">
                Name
              </label>
              <input
                type="text"
                id="edit-name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border rounded-md"
              />
            </div>

            <div className="mb-4">
              <label
                className="block text-gray-700 mb-2"
                htmlFor="edit-category"
              >
                Category
              </label>
              <select
                id="edit-category"
                name="category"
                value={formData.category}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border rounded-md"
              >
                <option value="">Select Category</option>
                {categories.map((category) => (
                  <option key={category} value={category}>
                    {category}
                  </option>
                ))}
                <option value="other">Other (New Category)</option>
              </select>

              {formData.category === 'other' && (
                <input
                  type="text"
                  name="newCategory"
                  placeholder="Enter new category"
                  value={newCategory}
                  onChange={handleNewCategoryChange}
                  className="w-full px-3 py-2 border rounded-md mt-2"
                />
              )}
            </div>

            <div className="mb-4">
              <label className="block text-gray-700 mb-2" htmlFor="edit-color">
                Color
              </label>
              <select
                id="edit-color"
                name="color"
                value={formData.color}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border rounded-md"
              >
                <option value="">Select Color</option>
                {colors.map((color) => (
                  <option key={color} value={color}>
                    {color}
                  </option>
                ))}
                <option value="other">Other (New Color)</option>
              </select>

              {formData.color === 'other' && (
                <input
                  type="text"
                  name="newColor"
                  placeholder="Enter new color"
                  value={newColor}
                  onChange={handleNewColorChange}
                  className="w-full px-3 py-2 border rounded-md mt-2"
                />
              )}
            </div>

            <div className="mb-4">
              <label className="block text-gray-700 mb-2" htmlFor="edit-price">
                Price Per Stem (₹)
              </label>
              <input
                type="number"
                id="edit-price"
                name="price_per_stem"
                value={formData.price_per_stem}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border rounded-md"
                min="0.01"
                step="0.01"
              />
            </div>

            <div className="mb-4">
              <label
                className="block text-gray-700 mb-2"
                htmlFor="edit-image-upload"
              >
                Flower Image
              </label>
              <div className="flex flex-col space-y-2">
                <input
                  type="file"
                  id="edit-image-upload"
                  name="image_upload"
                  accept="image/jpeg, image/png, image/gif, image/webp"
                  onChange={handleFileUpload}
                  className="w-full px-3 py-2 border rounded-md"
                />
                <small className="text-gray-500">
                  Supported formats: JPEG, PNG, GIF, WebP
                </small>

                {isUploading && (
                  <div className="w-full bg-gray-200 rounded-full h-2.5">
                    <div
                      className="bg-secondary h-2.5 rounded-full"
                      style={{ width: `${uploadProgress}%` }}
                    ></div>
                  </div>
                )}

                {formData.image_url && (
                  <div className="flex items-center mt-2">
                    <img
                      src={formData.image_url}
                      alt="Flower preview"
                      className="w-16 h-16 object-cover rounded mr-2"
                    />
                    <span className="text-sm text-gray-500 truncate flex-1">
                      {formData.image_url}
                    </span>
                  </div>
                )}

                <div className="text-sm text-gray-500">
                  Or provide an image URL:
                </div>

                <input
                  type="url"
                  id="edit-image"
                  name="image_url"
                  value={formData.image_url}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border rounded-md"
                  placeholder="https://example.com/image.jpg"
                />
              </div>
            </div>

            <div className="mb-4">
              <label
                className="block text-gray-700 mb-2"
                htmlFor="edit-available"
              >
                Availability
              </label>
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="edit-available"
                  name="available"
                  checked={formData.available}
                  onChange={handleInputChange}
                  className="mr-2"
                />
                <span>Available for purchase</span>
              </div>
            </div>
          </div>

          <div className="mb-4">
            <label
              className="block text-gray-700 mb-2"
              htmlFor="edit-description"
            >
              Description
            </label>
            <textarea
              id="edit-description"
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border rounded-md"
              rows="4"
            ></textarea>
          </div>

          <div className="flex justify-end">
            <button
              type="button"
              onClick={() => {
                setActiveTab('list');
                setSelectedFlower(null);
              }}
              className="px-4 py-2 bg-gray-300 text-gray-800 rounded-md mr-2"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-primary text-white rounded-md"
              disabled={loading}
            >
              {loading ? 'Updating...' : 'Update Flower'}
            </button>
          </div>
        </form>
      )}
    </div>
  );
};

export default AdminFlowerPage;
